import qs from 'qs';
import { User } from 'src/types/user';

import { api, publicApi } from '../../api/api';

export type WalletInvitationStatus =
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'canceled'
  | 'expired';

export type WalletInvitationLinkStatus =
  | 'draft'
  | 'pending'
  | 'accepted'
  | 'rejected';

export type WalletInvitation = {
  id: number;
  email: string;
  name?: string;
  status: WalletInvitationStatus;
  expiresIn?: string;
  code: string;
  createdAt: string;
  updatedAt: string;
  user?: User;
  owner?: User;
};

export const getInvitedUsers = async (
  ownerId?: number,
): Promise<WalletInvitation[]> => {
  const query = qs.stringify(
    {
      fields: [
        'id',
        'email',
        'name',
        'status',
        'expiresIn',
        'code',
        'createdAt',
        'updatedAt',
      ],
      sort: ['createdAt:desc'],
      filters: ownerId
        ? {
            owner: {
              id: ownerId,
            },
          }
        : {},
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get(`/wallet-invitations?${query}`);
  return data?.data || [];
};

export const inviteMonitorUser = async (name: string, email: string) => {
  const { data } = await api.post('/wallet-invitations/create', {
    name,
    email,
  });
  return data;
};

export const resendInvitation = async (email: string) => {
  const { data } = await api.post('/wallet-invitations/resend', {
    data: { email },
  });
  return data;
};

export const getInvitationById = async (
  id: number,
): Promise<WalletInvitation | null> => {
  const query = qs.stringify(
    {
      fields: ['id', 'status'],
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get(`/wallet-invitations/${id}?${query}`);
  return data?.data || null;
};

export const cancelInvitation = async (id: number): Promise<void> => {
  const invitation = await getInvitationById(id);

  if (!invitation) {
    throw new Error('Convite não encontrado');
  }

  // Atualiza apenas o status para canceled
  await api.put(`/wallet-invitations/${id}`, {
    data: {
      status: 'canceled',
    },
  });
};

export type WalletInvitationTokenData = {
  email: string;
  name: string;
  status: string;
  owner: User;
};

export const normalizeWalletInvitationEmail = (email?: string | null): string =>
  email?.trim().toLowerCase() ?? '';

export const isWalletInvitationEmailMatch = (
  invitedEmail?: string | null,
  authenticatedEmail?: string | null,
): boolean => {
  const normalizedInvitedEmail = normalizeWalletInvitationEmail(invitedEmail);
  const normalizedAuthenticatedEmail =
    normalizeWalletInvitationEmail(authenticatedEmail);

  return (
    !!normalizedInvitedEmail &&
    normalizedInvitedEmail === normalizedAuthenticatedEmail
  );
};

export const confirmWalletInvitation = async (
  token: string,
): Promise<WalletInvitationTokenData> => {
  const { data } = await api.post('/wallet-invitations/confirm', {
    token,
  });
  return data;
};

export const getWalletInvitationData = async (
  token: string,
): Promise<WalletInvitationTokenData | null> => {
  const { data } = await publicApi.get(
    `/wallet-invitations/get-wallet-invitation-data?token=${token}`,
  );
  return data?.invitation || null;
};
